import { PsychAppointmentBookingSource, MatchingProfileData, GetMatchesInput, getMatches, patientMatchWithProvider, isApiError, PatientMatchWithProviderErrorOutput } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { useL10n, useTabsSyncedCallbackTrigger } from '@uplift/hooks';
import { FindPsychiatrist } from '@uplift/pages';
import { useAvailability, useUserInsurancesQuery, useUserProfileQuery } from '@uplift/queries';
import { CalendarDateService, DateObject } from '@uplift/services';
import { ProviderMedicalRole } from '@uplift/types';
import { AutoLogoutTrigger, ContextualNavigationLayout, useAlert } from '@uplift/ui-v2';
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router';

const seed = Math.random();

export const FindPsychiatristPage: React.VFC<{ source?: PsychAppointmentBookingSource }> = ({ source }) => {
  const l10n = useL10n();
  const history = useHistory();
  const currentUser = useCurrentuser();
  const timezone = useTimezone();
  const snackbar = useAlert();
  const logoutTrigger = useTabsSyncedCallbackTrigger(AutoLogoutTrigger.logoutManual);
  const [ currentProvider, setCurrentProvider ] = useState<MatchingProfileData>(null);
  const [ selectedProvider, setSelectedProvider ] = useState<MatchingProfileData>(null);
  const [ appointmentTime, setAppointmentTime ] = useState<DateObject>(null);
  const { mutateAsync: addAppointment } = useMutation(patientMatchWithProvider);
  const { data: profile, isLoading: profileLoading } = useUserProfileQuery(currentUser.id, { enabled: !!currentUser.id });
  const { data: insurances, isLoading: insuranceLoading } = useUserInsurancesQuery(currentUser.id, { enabled: !!currentUser.id });
  const profileDataLoading = profileLoading || insuranceLoading;
  const patientStateId = profile?.address?.stateId;
  const patientInsuranceCompanyId = insurances?.[0]?.insurance?.companyId;
  const matchingQueryInput: GetMatchesInput = useMemo(() => ({
    patientId: currentUser.id,
    seed: seed,
    stateId: patientStateId,
    insuranceCompanyId: patientInsuranceCompanyId,
    medicalRole: ProviderMedicalRole.psychiatrist,
  }), [ currentUser.id, patientInsuranceCompanyId, patientStateId ]);

  const { data: providers, isInitialLoading: providersLoading } = useQuery(
    [ 'get-providers', matchingQueryInput ],
    () => getMatches(matchingQueryInput),
    { enabled: !profileDataLoading }
  );

  const handleBack = useCallback(() => {
    history.goBack();
  }, [ history ]);

  const handleLogout = useCallback(() => {
    logoutTrigger();
  }, [ logoutTrigger ]);

  const handleChangeProvider = useCallback((provider: MatchingProfileData | null) => {
    setCurrentProvider(provider);
  }, []);

  const { slots: availabilitySlots, slotsLoading, refetchSlots } = useAvailability({
    providerIds: providers?.data.matchingProfiles?.map(p => p.user.id) ?? [],
    patientId: currentUser.id,
  });

  const handleDialogClose = useCallback(async (confirm?: boolean) => {
    if (!confirm) {
      setSelectedProvider(null);
      setAppointmentTime(null);

      return;
    }

    try {
      const result = await addAppointment({
        providerId: selectedProvider.user.id,
        fromDatetime: appointmentTime.format(),
        psychiatryBookingSource: source,
      });

      if (result?.data?.created) {
        history.push('/main');
        snackbar.success('Consultation Scheduled');
      } else {
        snackbar.error(l10n.schedule.createAppointment.providerErrors.unknown.body);
      }
    } catch (err) {
      if (isApiError<PatientMatchWithProviderErrorOutput>(err)) {
        const errorMapping = l10n.schedule.createAppointment.errors;
        const errorKey = err.response?.data?.error;
        const mappedError = errorMapping[errorKey];

        if (mappedError) {
          refetchSlots(selectedProvider.user.id);
          setSelectedProvider(null);
          setAppointmentTime(null);
          snackbar.error(mappedError.body, {
            title: mappedError.title
          });
        } else {
          snackbar.error(l10n.schedule.createAppointment.providerErrors.unknown.body);
        }
      }
      throw err;
    }
  }, [ addAppointment, appointmentTime, history, selectedProvider?.user?.id, snackbar, source, l10n ]);

  const handleScheduleSession = useCallback((date: DateObject) => {
    const formattedDate = date.utc().format();

    const provider = availabilitySlots
      .filter(x => !currentProvider || currentProvider.user.id === x.providerId)
      .find(x => {
        if (x.slots.includes(formattedDate)) {
          return true;
        }

        return false;
      });

    if (provider) {
      setAppointmentTime(date);
      setSelectedProvider(providers?.data.matchingProfiles.find(x => x.user.id === provider.providerId));
    }
  }, [ availabilitySlots, currentProvider, providers?.data.matchingProfiles ]);

  const slots = useMemo(() => {
    const squashedSlots = availabilitySlots
      .filter(x => !currentProvider || currentProvider.user.id === x.providerId)
      .flatMap(x => x.slots)
      .reduce((acc, cur) => acc.add(cur), new Set<string>());

    return Array.from(squashedSlots).map(x => CalendarDateService.date(x).tz(timezone));
  }, [ availabilitySlots, currentProvider, timezone ]);


  const headerProps = useMemo(() => ({
    onLeftButtonClick: handleBack,
    leftButtonText: l10n.navigation.back,
    onRightButtonClick: handleLogout,
    rightButtonText: l10n.navigation.logout,
  }), [ handleBack, handleLogout, l10n.navigation.back, l10n.navigation.logout ]);

  return (
    <ContextualNavigationLayout headerProps={headerProps}>
      <FindPsychiatrist
        isLoading={profileDataLoading || providersLoading || slotsLoading}
        appointmentTime={appointmentTime}
        currentProvider={currentProvider}
        providers={providers?.data.matchingProfiles}
        selectedProvider={selectedProvider}
        timeSlots={slots}
        onDialogClose={handleDialogClose}
        onProviderSelect={handleChangeProvider}
        onScheduleSession={handleScheduleSession}
      />
    </ContextualNavigationLayout>
  );
};
